import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from '../api/axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

const ACTIVE_STATUSES = ['scheduled', 'confirmed', 'pending'];

const MyGraphBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Sample viewer. The sample is served through an authenticated route, so it
  // can't be dropped into a plain <img src="..."> — that request wouldn't
  // carry the Bearer token. It's fetched as a blob on demand instead, only
  // when the person actually asks to see it, not eagerly for the whole list.
  const [sampleTarget, setSampleTarget] = useState(null);
  const [sampleUrl, setSampleUrl] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/graph-bookings/mine');
      setBookings(Array.isArray(data.bookings) ? data.bookings : []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your handwriting analysis sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Revoke the blob URL whenever the viewer closes or swaps samples, or each
  // view leaks memory for the tab's lifetime — same pattern as the upload
  // preview in GraphBookingPage.
  useEffect(() => {
    return () => {
      if (sampleUrl) URL.revokeObjectURL(sampleUrl);
    };
  }, [sampleUrl]);

  const hoursUntil = (booking) => {
    const at = booking.appointment_time_raw || booking.appointment_time;
    return moment(at).diff(moment(), 'hours');
  };

  const isActive = (b) => ACTIVE_STATUSES.includes(String(b.status).toLowerCase());

  const openCancel = (booking) => {
    setCancelTarget({ ...booking, hours: hoursUntil(booking) });
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);

    try {
      await axios.patch(`/graph-bookings/${cancelTarget.id}/cancel`, {
        cancellationReason: 'Cancelled by client'
      });

      toast.success('Session cancelled.');
      setCancelTarget(null);
      fetchBookings();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('This booking has already been cancelled.');
        setCancelTarget(null);
        fetchBookings();
      } else {
        toast.error(err.response?.data?.error || 'Could not cancel this session.');
      }
    } finally {
      setCancelling(false);
    }
  };

  const openSample = async (booking) => {
    setSampleTarget(booking);
    setSampleUrl(null);
    setSampleError(null);
    setSampleLoading(true);

    try {
      const { data } = await axios.get(`/graph-bookings/${booking.id}/handwriting-sample`, {
        responseType: 'blob'
      });
      setSampleUrl(URL.createObjectURL(data));
    } catch (err) {
      setSampleError(
        err.response?.status === 404
          ? 'Sample not found.'
          : 'Could not load your handwriting sample.'
      );
    } finally {
      setSampleLoading(false);
    }
  };

  const closeSample = () => {
    setSampleTarget(null);
    if (sampleUrl) URL.revokeObjectURL(sampleUrl);
    setSampleUrl(null);
    setSampleError(null);
  };

  const upcoming = bookings.filter(b => isActive(b) && hoursUntil(b) > -2);
  const past = bookings.filter(b => !isActive(b) || hoursUntil(b) <= -2);

  if (loading) {
    return <div style={styles.centered}><p>Loading your sessions…</p></div>;
  }

  if (error) {
    return (
      <div style={styles.centered}>
        <p style={{ color: '#c0392b' }}>{error}</p>
        <button onClick={fetchBookings} style={styles.primaryBtn}>Try again</button>
      </div>
    );
  }

  const BookingCard = ({ booking, actionable }) => {
    const at = booking.appointment_time_raw || booking.appointment_time;

    return (
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <img
            src={booking.graphologist_image ? `${BACKEND_URL}/uploads/${booking.graphologist_image}` : '/img/6971412.png'}
            alt={booking.graphologist_name}
            onError={(e) => { e.target.src = '/img/6971412.png'; }}
            style={styles.avatar}
          />
          <div style={{ flex: 1 }}>
            <h4 style={styles.name}>{booking.graphologist_name}</h4>
            <p style={styles.meta}>{moment(at).format('dddd, MMMM D · h:mm A')}</p>
            {booking.specialisation && (
              <p style={styles.meta}>{booking.specialisation}</p>
            )}
          </div>
          <span style={{
            ...styles.statusPill,
            background: isActive(booking) ? 'rgba(74,124,111,0.12)' : 'rgba(48,60,108,0.08)',
            color: isActive(booking) ? '#4a7c6f' : '#6b7280'
          }}>
            {booking.status}
          </span>
        </div>

        {booking.status === 'completed' && booking.report_notes && (
          <p style={styles.notes}>{booking.report_notes}</p>
        )}

        <div style={styles.actions}>
          <button onClick={() => openSample(booking)} style={styles.secondaryBtn}>
            View sample
          </button>
          {actionable && (
            <button onClick={() => openCancel(booking)} style={styles.dangerBtn}>
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <ToastContainer position="top-center" />

      <div style={styles.headerRow}>
        <h2 style={styles.pageTitle}>Handwriting Analysis</h2>
        <Link to="/my-bookings" style={styles.tabLink}>← My Sessions</Link>
      </div>

      <p style={styles.policyNote}>
        There's no rescheduling for these sessions yet — you can cancel and book a new time instead.
      </p>

      <h3 style={styles.sectionTitle}>Upcoming</h3>
      {upcoming.length === 0 ? (
        <div style={styles.empty}>
          <p>No upcoming handwriting analysis sessions.</p>
          <Link to="/doctors?tab=graphologists" style={styles.primaryBtn}>Find a graphologist</Link>
        </div>
      ) : (
        upcoming.map(b => <BookingCard key={b.id} booking={b} actionable />)
      )}

      <h3 style={styles.sectionTitle}>Past</h3>
      {past.length === 0 ? (
        <p style={styles.meta}>Nothing here yet.</p>
      ) : (
        past.map(b => <BookingCard key={b.id} booking={b} actionable={false} />)
      )}

      {/* ── Cancel confirmation ── */}
      {cancelTarget && (
        <div style={styles.overlay} onClick={() => !cancelling && setCancelTarget(null)}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <h4 style={styles.dialogTitle}>Cancel this session?</h4>

            <p style={styles.dialogBody}>
              {moment(cancelTarget.appointment_time_raw || cancelTarget.appointment_time)
                .format('dddd, MMMM D · h:mm A')} with {cancelTarget.graphologist_name}.
            </p>

            <div style={styles.dialogActions}>
              <button
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                style={styles.secondaryBtn}
              >
                Keep session
              </button>
              <button onClick={confirmCancel} disabled={cancelling} style={styles.dangerBtn}>
                {cancelling ? 'Cancelling…' : 'Cancel session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Handwriting sample viewer ── */}
      {sampleTarget && (
        <div style={styles.overlay} onClick={closeSample}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <h4 style={styles.dialogTitle}>Your handwriting sample</h4>

            {sampleLoading ? (
              <p style={styles.meta}>Loading…</p>
            ) : sampleError ? (
              <p style={{ color: '#c0392b' }}>{sampleError}</p>
            ) : sampleUrl ? (
              <img
                src={sampleUrl}
                alt="Your submitted handwriting sample"
                style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 12, display: 'block', margin: '0 auto' }}
              />
            ) : null}

            <div style={styles.dialogActions}>
              <button onClick={closeSample} style={styles.secondaryBtn}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: { maxWidth: 760, margin: '40px auto', padding: '0 24px' },
  centered: { textAlign: 'center', padding: '80px 24px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 12 },
  pageTitle: { fontFamily: "'DM Serif Display', serif", color: '#1a2344', margin: 0 },
  tabLink: { color: '#e8622a', textDecoration: 'none', fontWeight: 600, fontSize: 14 },
  policyNote: { color: '#6b7280', fontSize: 14, marginTop: 8, marginBottom: 32 },
  sectionTitle: { fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6b7280', marginTop: 32, marginBottom: 12 },
  card: { background: 'white', borderRadius: 16, padding: 20, marginBottom: 12, border: '1px solid rgba(48,60,108,0.08)', boxShadow: '0 4px 12px rgba(48,60,108,0.04)' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 14 },
  avatar: { width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  name: { margin: 0, fontFamily: "'DM Serif Display', serif", fontSize: 18, color: '#1a2344' },
  meta: { margin: '2px 0 0', color: '#6b7280', fontSize: 14 },
  notes: { marginTop: 14, marginBottom: 0, fontSize: 13, color: '#374151', background: '#fafbfc', padding: '10px 12px', borderRadius: 10 },
  statusPill: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 50 },
  actions: { display: 'flex', gap: 10, marginTop: 16 },
  empty: { textAlign: 'center', padding: '32px 20px', background: '#fafbfc', borderRadius: 16, color: '#6b7280' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(26,35,68,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 },
  dialog: { background: 'white', borderRadius: 20, padding: 32, maxWidth: 480, width: '100%', maxHeight: '80vh', overflowY: 'auto' },
  dialogTitle: { fontFamily: "'DM Serif Display', serif", margin: '0 0 12px', color: '#1a2344' },
  dialogBody: { color: '#374151', fontSize: 15, margin: '0 0 12px' },
  dialogActions: { display: 'flex', gap: 10, marginTop: 20 },
  primaryBtn: { flex: 1, padding: '12px 20px', borderRadius: 50, border: 'none', background: '#1a2344', color: 'white', fontWeight: 600, fontSize: 14, cursor: 'pointer', textDecoration: 'none', display: 'inline-block', textAlign: 'center' },
  secondaryBtn: { flex: 1, padding: '12px 20px', borderRadius: 50, border: '1.5px solid rgba(48,60,108,0.15)', background: 'white', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  dangerBtn: { flex: 1, padding: '12px 20px', borderRadius: 50, border: 'none', background: '#fee2e2', color: '#c0392b', fontWeight: 600, fontSize: 14, cursor: 'pointer' }
};

export default MyGraphBookings;